import type { ServerEvent } from "@shared/api";

/** Без назначенного конца событие идёт 12 часов — так же считает сервер. */
const OPEN_ENDED = 12 * 60 * 60 * 1000;

export function endsAt(event: Pick<ServerEvent, "starts" | "ends">): number {
  return event.ends ? Date.parse(event.ends) : Date.parse(event.starts) + OPEN_ENDED;
}

/** Идёт ли событие прямо сейчас. */
export function isLive(event: Pick<ServerEvent, "starts" | "ends">, now = Date.now()): boolean {
  return Date.parse(event.starts) <= now && now < endsAt(event);
}

const time = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const day = new Intl.DateTimeFormat("ru-RU", { weekday: "short", day: "numeric", month: "short" });

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** «Сегодня, 19:30», «Завтра, 10:00» или «пт, 31 окт., 19:30». */
export function whenLabel(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const prefix = sameDay(at, now) ? "Сегодня" : sameDay(at, tomorrow) ? "Завтра" : day.format(at);
  return `${prefix}, ${time.format(at)}`;
}

/** Локальные дата и время из полей формы — в ISO; `null` — поле пустое или кривое. */
export function fromLocal(date: string, clock: string): string | null {
  if (!date || !clock) return null;
  const at = new Date(`${date}T${clock}`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** Значения для полей `date` и `time` по местному времени. */
export function toLocal(iso: string | Date): { date: string; clock: string } {
  const at = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    clock: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  };
}

/** Через час от сейчас, ровно в начале часа — так форма нового события открывается. */
export function nextHour(now = new Date()): { date: string; clock: string } {
  const at = new Date(now);
  at.setMinutes(0, 0, 0);
  at.setHours(at.getHours() + 1);
  return toLocal(at);
}

/** Длительность в целых часах; 0 — без конца. */
export function lengthOf(event: Pick<ServerEvent, "starts" | "ends">): number {
  return event.ends ? Math.max(1, Math.round((Date.parse(event.ends) - Date.parse(event.starts)) / 3_600_000)) : 0;
}
